export enum IsoPrecision {
  Days = 'YYYY-MM-DD',
  Hours = 'YYYY-MM-DDTHH',
  Seconds = 'YYYY-MM-DDTHH:mm:ss',
  Milliseconds = 'YYYY-MM-DDTHH:mm:ss.sss',
}
export enum DateParsingPattern {
  YYMMDD = 'YYMMDD',
}

export class DateHelper {
  private static readonly DefaultUTCTime = 'T00:00:00.000Z';
  private static readonly DefaultYYMMDDString = '000101';
  private static readonly MS_PER_DAY = 86_400_000;
  private static readonly LengthYYMMDDString = 6;

  /**
   * Returns the local UTC offset in the format `UTC±H`.
   * For example, an offset of -60 minutes becomes `UTC+1`.
   * Note: Uses the proper minus sign `−` for negative offsets.
   *
   * @param date - A date object (defaults to current date if not provided)
   * @returns A string like `UTC+1` or `UTC−2`
   */
  static getLocalUtcOffsetString(date: Date = new Date()): string {
    const offsetMinutes = date.getTimezoneOffset(); // in minutes
    const offsetHours = -offsetMinutes / 60; // flip sign: -60 → +1
    const sign = offsetHours >= 0 ? '+' : '−'; // proper minus sign
    return `UTC${sign}${Math.abs(offsetHours)}`;
  }

  /**
   * Converts days to milliseconds.
   * @param days - The number of days to convert.
   * @returns The equivalent time in milliseconds.
   */
  static daysToMilliseconds(days: number): number {
    return days * this.MS_PER_DAY;
  }

  /**
   * Formats a Date object into ISO 8601 format with configurable precision.
   * @param date - The Date object to format.
   * @param precision - The desired precision: Days, Hours, Seconds, or Milliseconds.
   * @returns A string in the ISO 8601 format with the specified precision.
   */
  static toISOStringWithPrecision(
    date: Date,
    precision: IsoPrecision = IsoPrecision.Seconds
  ): string {
    const isoString = date.toISOString(); // Always returns full precision: YYYY-MM-DDTHH:mm:ss.sssZ

    switch (precision) {
      case IsoPrecision.Days:
        return isoString.slice(0, 10) + 'Z'; // YYYY-MM-DDZ
      case IsoPrecision.Hours:
        return isoString.slice(0, 13) + 'Z'; // YYYY-MM-DDTHHZ
      case IsoPrecision.Seconds:
        return isoString.slice(0, 19) + 'Z'; // YYYY-MM-DDTHH:mm:ssZ
      case IsoPrecision.Milliseconds:
      default:
        return isoString; // Full ISO format YYYY-MM-DDTHH:mm:ss.sssZ
    }
  }

  /**
   * Validates a date input string and determines its validity based on a specified parsing pattern.
   *
   * The function supports different date formats, such as `YYMMDD` (two-digit year) and `YYYYMMDD` (four-digit year),
   * and dynamically adapts its validation based on the provided `dateParsingPattern`.
   *
   * @param dateInput - A string representing a date, formatted according to `dateParsingPattern`.
   * @param dateParsingPattern - The expected format of `dateInput`. Defaults to `YYMMDD`.
   * @returns An object containing:
   *   - `isDateFullyMatchingInput`: Whether the input is fully valid and complete according to the expected format.
   *   - `isDateMatchingInput`: Whether the input forms a valid date structure.
   *   - `date`: A `Date` object if the input is fully valid; otherwise, `null`.
   *
   * @throws Error if an unsupported `dateParsingPattern` is provided.
   */
  static validateDateInput(
    dateInput: string,
    dateParsingPattern: DateParsingPattern = DateParsingPattern.YYMMDD
  ): {
    isDateFullyMatchingInput: boolean;
    isDateMatchingInput: boolean;
    date: Date | null;
  } {
    // Determine if the date input is partial (not fully provided)
    const expectedLength = dateParsingPattern.length;
    const isCompleteInput: boolean = dateInput.length !== expectedLength;

    const { year, month, day } = this.parseAndCompleteDate(dateInput);
    const parsedDate: Date = new Date(
      Date.UTC(year, month - 1, day, 0, 0, 0, 0)
    );

    const isDateComponentsValid: boolean =
      parsedDate.getFullYear() === year &&
      parsedDate.getMonth() === month - 1 &&
      parsedDate.getDate() === day;

    const isDateMatchingInput: boolean =
      isDateComponentsValid &&
      this.GetDateAsYYMMDD(parsedDate).startsWith(dateInput);
    const isDateFullyMatchingInput: boolean =
      !isCompleteInput && isDateMatchingInput;

    const validatedDate: Date | null = isDateFullyMatchingInput
      ? parsedDate
      : null;

    return {
      isDateFullyMatchingInput: isDateFullyMatchingInput,
      isDateMatchingInput: isDateMatchingInput,
      date: validatedDate,
    };
  }

  private static parseAndCompleteDate(
    dateInput: string,
    dateParsingPattern: DateParsingPattern = DateParsingPattern.YYMMDD
  ) {
    let year: number, month: number, day: number;
    if (dateParsingPattern == DateParsingPattern.YYMMDD) {
      const yearLastTwoDigits: string = dateInput.slice(0, 2);
      const monthAsTwoDigits: string = dateInput.slice(2, 4);
      const dayAsTwoDigits: string = dateInput.slice(4, 6);
      year = this.parseAndCompleteFullYear(yearLastTwoDigits);
      month = this.parseAndCompleteMonth(monthAsTwoDigits);
      day = this.parseAndCompleteDay(dayAsTwoDigits);
    } else {
      throw new Error('Unsupported date parsing pattern');
    }

    return { year, month, day };
  }

  private static parseAndCompleteFullYear(
    yearLastTwoDigitsInput: string
  ): number {
    const getYearLastTwoDigitsString = (yearInput: string): string => {
      if (yearInput.length == 0) return '00';
      if (yearInput.length == 1) return yearInput.padEnd(2, '0');
      return yearInput;
    };
    const parsedYearLastTwoDigits: number = parseInt(
      getYearLastTwoDigitsString(yearLastTwoDigitsInput),
      10
    );
    const centuryPrefix: number = this.getCenturyPrefix(
      parsedYearLastTwoDigits
    );

    return centuryPrefix * 100 + parsedYearLastTwoDigits;
  }

  private static parseAndCompleteMonth(monthAsTwoDigitsInput: string): number {
    const getMonthString = (monthInput: string): string => {
      if (monthInput.length == 0) return '01';
      if (monthInput.length == 1)
        return parseInt(monthInput) == 0
          ? monthInput.padEnd(2, '1')
          : monthInput.padEnd(2, '0');
      return monthInput;
    };

    return parseInt(getMonthString(monthAsTwoDigitsInput), 10);
  }

  private static parseAndCompleteDay(dayAsTwoDigitsInput: string): number {
    const getDayString = (dayInput: string): string => {
      if (dayInput.length == 0) return '01';
      if (dayInput.length == 1)
        return parseInt(dayInput) == 0
          ? dayInput.padEnd(2, '1')
          : dayInput.padEnd(2, '0');
      return dayInput;
    };

    return parseInt(getDayString(dayAsTwoDigitsInput), 10);
  }

  static GetDateAsYYMMDD(date: Date = new Date()): string {
    return (
      date.getFullYear().toString().substring(2).padStart(2, '0') +
      (date.getMonth() + 1).toString().padStart(2, '0') +
      date.getDate().toString().padStart(2, '0')
    );
  }

  static GetDateOnlyFromUTCDateYYMMDD(UTCDateAsYYMMDD: string): Date {
    const shortYear = parseInt(UTCDateAsYYMMDD.slice(0, 2), 10);
    const month = parseInt(UTCDateAsYYMMDD.slice(2, 4), 10);
    const day = parseInt(UTCDateAsYYMMDD.slice(4, 6), 10);

    const formatToTwoDigits = (value: number): string =>
      value.toString().padStart(2, '0');
    const fullYearString = this.buildFullYear(shortYear);
    const monthString = formatToTwoDigits(month);
    const dayString = formatToTwoDigits(day);

    const unvalidatedCalendarDateUTCString = `${fullYearString}-${monthString}-${dayString}${this.DefaultUTCTime}`;
    const unvalidatedIsoDate = new Date(unvalidatedCalendarDateUTCString);

    // Protection: Check if the constructed date matches the input components
    const isValidConvertion = (
      convertedDate: Date,
      dateSource: { day: number; month: number }
    ) => {
      const { day, month } = dateSource;
      return (
        convertedDate.getDate() === day &&
        convertedDate.getMonth() + 1 === month
      );
    };

    return isValidConvertion(unvalidatedIsoDate, { day, month })
      ? unvalidatedIsoDate
      : new Date(undefined as any);
  }

  static IsValidDate(date: Date): boolean {
    return !isNaN(date.getTime());
  }

  static GetISODateOnlyStringFromUTCDate(utcDate: Date): string {
    if (!utcDate || isNaN(utcDate.getTime())) {
      throw new Error('Invalid Date provided for conversion.');
    }

    const result: string = utcDate.toISOString().split('T')[0];
    if (result.length != 8 + 2) {
      throw new Error('Invalid Date provided for conversion.');
    }

    return result;
  }

  static GetRegionalDateFromISODateOnlyString(ISODateOnlyString: string): Date {
    const [year, month, day] = ISODateOnlyString.split('T')[0]
      .split('-')
      .map((entry) => Number(entry));
    const utcDate: Date = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));

    return utcDate;
  }

  static GetRegionalDateFromISODateString(ISODateString: string): Date {
    const [yearMonthDayString, timeString] = ISODateString.split('T');
    const [year, month, day] = yearMonthDayString
      .split('-')
      .map((entry) => Number(entry));
    const [hh, mm, ss] = timeString
      .replace('Z', '')
      .split(':')
      .map((entry) => Number(entry));
    const utcDate: Date = new Date(
      Date.UTC(year, month - 1, day, hh, mm, ss, 0)
    );

    return utcDate;
  }

  private static buildFullYear(
    lastTwoYearDigits: number,
    centuryPrefix: number = this.getCenturyPrefix(lastTwoYearDigits)
  ): number {
    return Number(
      `${centuryPrefix}${lastTwoYearDigits.toString().padStart(2, '0')}`
    );
  }

  private static getCenturyPrefix(lastTwoYearDigits: number): number {
    return lastTwoYearDigits < 75 ? 20 : 19;
  }

  // INFO Experimental - experiment 001
  static ValidatePartialDateFromUTCDateYYMMDD(
    partialUTCDateAsYYMMDD: string
  ): boolean {
    const UTCDateAsYYMMDD: string = this.autofillYYMMDD(partialUTCDateAsYYMMDD);
    const dateToValidate: Date =
      this.GetDateOnlyFromUTCDateYYMMDD(UTCDateAsYYMMDD);
    return !isNaN(dateToValidate.getTime());
  }

  // INFO Experimental - experiment 001
  private static autofillYYMMDD(
    partialUTCDateAsYYMMDD: string,
    defaultYYMMDDString = this.DefaultYYMMDDString
  ) {
    if (defaultYYMMDDString.length != 6)
      throw Error('Unexpected length for defaultInput');

    const expectedDateLength = defaultYYMMDDString.length;
    const inputLength = partialUTCDateAsYYMMDD.length;

    // Generate an unvalidated result by appending the fallback date
    const unvalidatedDate =
      inputLength + defaultYYMMDDString.slice(inputLength, expectedDateLength);

    // Check if the placeholder day is unsafe (e.g., 31st day in a month with fewer than 31 days)
    const hasUnsafePlaceholderDay =
      inputLength < expectedDateLength &&
      Number(unvalidatedDate.slice(4, 6)) === 31;

    // If the day is unsafe, change it to the 30th
    const validatedDate = hasUnsafePlaceholderDay
      ? unvalidatedDate.slice(0, 4) + '30'
      : unvalidatedDate;

    return validatedDate;
  }
}
